import { Button, cn } from '@heroui/react'
import type { ModalData } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useState } from 'react'

export default function ConfirmationModal() {
  const { data, setToggleModel } = useActiveModelStore()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const config: ModalData = data

  type ActionType = NonNullable<ModalData['actions']>[number]

  const handleAction = async (action: ActionType) => {
    if (action.onPress) {
      try {
        setIsSubmitting(true)
        await action.onPress()
        setToggleModel(false)
      } catch (error) {
        console.error('Action failed:', error)
        // Don't close modal on error, let user try again
      } finally {
        setIsSubmitting(false)
      }
    } else {
      setToggleModel(false)
    }
  }

  const getSubmitButton = () => {
    const submitAction = config?.actions?.find(
      action =>
        action.color === 'danger' ||
        action.color === 'primary' ||
        action.color === 'warning' ||
        action.color === 'success' ||
        action.variant === 'solid',
    )

    return submitAction
  }

  const submitButton = getSubmitButton()

  return (
    <div className={cn('', config?.classNames?.container)}>
      <div className={cn('relative z-10 flex flex-col justify-center items-center')}>
        <div
          className={cn(
            'w-[390px] my-11 flex flex-col justify-center items-center text-center gap-5',
            config?.classNames?.wrapper,
          )}
        >
          {config?.icon && (
            <div
              className={cn(
                'w-16 h-16 bg-gradient-to-r rounded-full flex items-center justify-center',
                config?.classNames?.iconWrapper,
              )}
            >
              <img
                alt="icon"
                className={cn('w-14 h-14', config?.classNames?.icon)}
                src={config.icon}
              />
            </div>
          )}
          {config?.title && (
            <h1
              style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              className={cn(
                'font-medium text-xl break-words max-w-full',
                config?.classNames?.title,
              )}
            >
              {config.title}
            </h1>
          )}

          {config?.body && (
            <div
              className={cn(
                'text-gray-500 dark:text-gray-300 leading-relaxed break-words',
                config?.classNames?.body,
              )}
            >
              {typeof config.body === 'function' ? config.body(config) : config.body}
            </div>
          )}
          {config?.message && (
            <p
              className={cn(
                'text-gray-500 dark:text-gray-300 leading-relaxed break-words',
                config?.classNames?.message,
              )}
            >
              {config.message}
            </p>
          )}
        </div>
      </div>

      <div className={cn('flex justify-end pb-4 gap-3', config?.classNames?.actions)}>
        {config?.actions &&
          config?.actions?.length > 0 &&
          config?.actions?.map((action, index) => {
            const isSubmitButton = action === submitButton
            const isDisabled = action.disabled || (isSubmitting && isSubmitButton)

            return (
              <Button
                key={index}
                color={action.color || 'default'}
                isDisabled={isDisabled}
                isLoading={isSubmitting && isSubmitButton}
                radius="sm"
                variant={action.variant || 'solid'}
                {...({ onPress: () => void handleAction(action) } as any)}
              >
                {action.label}
              </Button>
            )
          })}
      </div>
    </div>
  )
}
